// FACIT (golden baseline) — extraction of the activity-wizard's create_activity_v2 argument
// assembly, lifted VERBATIM from ActivityWizard.tsx (the inline object at submit). This is pure
// insurance: byte-identical output, ZERO behavior change. Once snapshot-pinned, no future redesign
// can silently change a created activity or a charged amount without turning a test red.
//
// ⚠️ UNIT TRAP (recon): the wizard carries organizerFeeMinor in ÖRE and passes it RAW here
// (organizer_fee_per_person_minor: fd.organizerFeeMinor ?? 0 — NO toMinor). The LEGACY path
// (createActivityPayload.ts) carries organizerFee in KRONOR via toMinor(). They are two separate
// contracts — never cross them with a find/replace.
//
// The `??` fallbacks below are LOAD-BEARING money/refund logic — preserve verbatim:
//   organizer_fee_per_person_minor ?? 0 (raw öre) · apply_member_discount ?? true (opposite of the
//   DB column default false) · payment_mode ?? 'system' · display_price_minor only when external
//   (else null) · cancellation_hours only when system && != null (else null; wizard-ONLY field).
//
// Court-resolution + the supabase.rpc call itself stay in ActivityWizard.tsx — only the argument
// object is extracted (courtIds + spotsTotal are computed there and passed IN).

import type { ActivityWizardFormData } from "@/components/activity/wizard/ActivityWizardContext";
import type { ActivityType } from "@/hooks/useActivityTypes";

export interface BuildWizardActivityPayloadCtx {
  /** organization.id — forced as p_org; organization_id is re-forced server-side in v2. */
  organizationId: string;
  /** the resolved activity type (selectedType) — supplies match_type + tournament fields. */
  selectedType: Pick<ActivityType, "slug" | "tournament_format">;
  /** courts resolved by ActivityWizard's court-resolution (auto-pick or specific slice). */
  courtIds: string[];
  /** fd.spotsTotal ?? 4, already defaulted in the submit handler. */
  spotsTotal: number;
}

export interface WizardCreateActivityPayload {
  p_org: string;
  p_activity: Record<string, unknown>;
  p_court_ids: string[];
  p_status: string;
  p_is_tournament: boolean;
  p_tournament_format: string | null;
  p_points_per_match: number | null;
}

/**
 * Map wizard form-state → create_activity_v2 RPC arguments. Pure + deterministic
 * (snapshot-pinned in buildWizardActivityPayload.test.ts). Output is byte-identical to the
 * former inline object in ActivityWizard.tsx — this is a facit, not a behavior change.
 */
export function buildWizardActivityPayload(
  fd: ActivityWizardFormData,
  ctx: BuildWizardActivityPayloadCtx,
): WizardCreateActivityPayload {
  const { organizationId, selectedType, courtIds, spotsTotal } = ctx;

  return {
    p_org: organizationId,
    p_activity: {
      activity_type_id: fd.activityTypeId,
      match_type: selectedType.slug,
      sport_type: fd.sport_type ?? "padel",
      date: fd.date,
      start_time: fd.startTime,
      end_time: fd.endTime,
      spots_total: spotsTotal,
      min_participants: fd.minParticipants ?? null,
      min_level: fd.minLevel ?? 1,
      max_level: fd.maxLevel ?? 10,
      visibility: fd.visibility ?? "public",
      activity_title: fd.activityTitle ?? null,
      description: fd.description ?? null,
      includes: fd.includes && fd.includes.length > 0 ? fd.includes : null,
      // step 5 (3f): fee-led; apply_member_discount sent EXPLICITLY (UI default ON).
      organizer_fee_per_person_minor: fd.organizerFeeMinor ?? 0,
      apply_member_discount: fd.applyMemberDiscount ?? true,
      payment_mode: fd.paymentMode ?? "system",
      display_price_minor:
        fd.paymentMode === "external" ? (fd.displayPriceMinor ?? 0) : null,
      // 2e-core: per-akt cancellation-fönster — meningsfullt bara för system-läge (betal-
      // kontext). null → create_activity_v2 löser org ?? 24 (frozen-stämpling vid CREATE).
      cancellation_hours:
        fd.paymentMode === "system" && fd.cancellationHours != null
          ? fd.cancellationHours
          : null,
    },
    p_court_ids: courtIds,
    p_status: "open",
    p_is_tournament: !!selectedType.tournament_format,
    p_tournament_format: selectedType.tournament_format ?? null,
    p_points_per_match: fd.targetPoints ?? null,
  };
}
